// components/DataEntry/DetailDrawer.jsx
// Slide-in drawer showing transaction-level detail records for a clicked KPI.
import React from "react";
import { formatDate } from "../../utils/helpers";

const STATUS_COLORS = {
  approved:           { bg: "#DCFCE7", text: "#16A34A" },
  "pending-execution":{ bg: "#FEF9C3", text: "#A16207" },
  pending:            { bg: "#FEF3C7", text: "#D97706" },
  open:               { bg: "#EFF6FF", text: "#2563EB" },
  receipted:          { bg: "#F0FDF4", text: "#15803D" },
  active:             { bg: "#F0F9FF", text: "#0284C7" },
  onboarded:          { bg: "#ECFDF5", text: "#059669" },
  issued:             { bg: "#F5F3FF", text: "#7C3AED" },
  meeting:            { bg: "#EFF6FF", text: "#1D4ED8" },
  negotiation:        { bg: "#FFF7ED", text: "#C2410C" },
  community:          { bg: "#ECFDF5", text: "#065F46" },
  regulatory:         { bg: "#FDF4FF", text: "#7E22CE" },
  presentation:       { bg: "#EFF6FF", text: "#1D4ED8" },
  workshop:           { bg: "#F0FDF4", text: "#15803D" },
  briefing:           { bg: "#FFFBEB", text: "#92400E" },
  review:             { bg: "#F5F3FF", text: "#6D28D9" },
  onboarding:         { bg: "#ECFDF5", text: "#065F46" },
  Major:              { bg: "#FEF2F2", text: "#DC2626" },
  Minor:              { bg: "#EFF6FF", text: "#2563EB" },
  RFP:                { bg: "#F5F3FF", text: "#7C3AED" },
  RFQ:                { bg: "#EFF6FF", text: "#1D4ED8" },
  EOI:                { bg: "#FFF7ED", text: "#C2410C" },
  quarterly:          { bg: "#F0FDF4", text: "#15803D" },
  monthly:            { bg: "#EFF6FF", text: "#2563EB" },
  cancelled:          { bg: "#FEF2F2", text: "#DC2626" },
  closed:             { bg: "#F3F4F6", text: "#6B7280" },
  "on hold":          { bg: "#FEF9C3", text: "#A16207" },
  revised:            { bg: "#EFF6FF", text: "#1D4ED8" },
  reviewed:           { bg: "#F0FDF4", text: "#15803D" },
  ncr:                { bg: "#FFF7ED", text: "#C2410C" },
  cpr:                { bg: "#F5F3FF", text: "#6D28D9" },
  "cost avoidance":   { bg: "#ECFDF5", text: "#059669" },
};

function Badge({ value }) {
  const style = STATUS_COLORS[value] || { bg: "#F3F4F6", text: "#374151" };
  return (
    <span style={{ background: style.bg, color: style.text, padding: "2px 9px", borderRadius: 20, fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" }}>
      {value}
    </span>
  );
}

function formatCell(value, type) {
  if (value == null || value === "") return <span style={{ color: "#D1D5DB" }}>—</span>;
  if (type === "currency")     return <span style={{ fontWeight: 700, color: "#0052CC" }}>${Number(value).toLocaleString()}</span>;
  if (type === "currency_ngn") return <span style={{ fontWeight: 700, color: "#0052CC" }}>₦{Number(value).toLocaleString()}</span>;
  if (type === "number")   return <span style={{ fontWeight: 600 }}>{Number(value).toLocaleString()}</span>;
  if (type === "date")     return <span style={{ fontSize: 12, color: "#374151" }}>{formatDate(value) || value}</span>;
  if (type === "badge")    return <Badge value={value} />;
  if (type === "percentage") return <span style={{ fontWeight: 600, color: "#0052CC" }}>{(Number(value) * 100).toFixed(0)}%</span>;
  // text
  return <span style={{ fontSize: 12, color: "#374151", maxWidth: 260, display: "inline-block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={value}>{value}</span>;
}

export default function DetailDrawer({ detail, onClose }) {
  // detail = { type, columns, rows, metricLabel, weekLabel }
  if (!detail) return null;

  const { type, columns, rows, metricLabel, weekLabel } = detail;
  const isEmpty = !rows || rows.length === 0;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{ position: "fixed", inset: 0, zIndex: 998, background: "rgba(0,15,40,0.45)", backdropFilter: "blur(3px)" }}
      />
      {/* Drawer */}
      <div style={{
        position: "fixed", top: 0, right: 0, bottom: 0, zIndex: 999,
        width: "min(780px,100vw)", background: "#fff",
        boxShadow: "-8px 0 40px rgba(0,0,0,0.18)",
        display: "flex", flexDirection: "column",
        fontFamily: "'Segoe UI',system-ui,sans-serif",
      }}>
        {/* Header */}
        <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid #F0F0F0", flexShrink: 0, background: "linear-gradient(135deg,#001A3D,#003080)" }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
            <div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.6)", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>
                Detail Records
              </div>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: "#fff" }}>{metricLabel || type}</h2>
              {weekLabel && (
                <p style={{ margin: "4px 0 0", fontSize: 12, color: "rgba(255,255,255,0.7)" }}>
                  📅 {weekLabel} · {rows.length} record{rows.length !== 1 ? "s" : ""}
                </p>
              )}
            </div>
            <button
              onClick={onClose}
              style={{ width: 34, height: 34, borderRadius: 9, border: "1.5px solid rgba(255,255,255,0.25)", background: "rgba(255,255,255,0.1)", cursor: "pointer", fontSize: 18, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
            >×</button>
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: 24 }}>
          {isEmpty ? (
            <div style={{ textAlign: "center", padding: "60px 24px" }}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>📭</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#374151", marginBottom: 6 }}>No records found</div>
              <div style={{ fontSize: 13, color: "#9CA3AF" }}>No {type.toLowerCase()} transactions for this period.</div>
            </div>
          ) : columns.length === 0 ? (
            <pre style={{ fontSize: 13, color: "#374151" }}>{JSON.stringify(rows, null, 2)}</pre>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ borderCollapse: "collapse", width: "max-content", minWidth: "100%", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "#F8F9FA" }}>
                    <th style={{ ...thStyle, minWidth: 36 }}>#</th>
                    {columns.map((col) => (
                      <th key={col.key} style={{ ...thStyle, minWidth: col.type === "text" ? 180 : 110 }}>{col.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={row.id || i} style={{ borderBottom: "1px solid #F3F4F6", background: i % 2 === 0 ? "#fff" : "#FAFBFD" }}>
                      <td style={{ ...tdStyle, color: "#9CA3AF", fontSize: 11, textAlign: "center", fontWeight: 600 }}>{i + 1}</td>
                      {columns.map((col) => (
                        <td key={col.key} style={{ ...tdStyle, verticalAlign: "top" }}>
                          {formatCell(row[col.key], col.type)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: "14px 24px", borderTop: "1px solid #F0F0F0", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between", background: "#FAFBFC" }}>
          <span style={{ fontSize: 12, color: "#9CA3AF" }}>{rows.length} {type} record{rows.length !== 1 ? "s" : ""}</span>
          <button
            onClick={onClose}
            style={{ padding: "9px 22px", borderRadius: 10, border: "1.5px solid #E5E7EB", background: "#fff", color: "#374151", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
          >
            Close
          </button>
        </div>
      </div>
    </>
  );
}

const thStyle = { padding: "10px 14px", textAlign: "left", fontWeight: 700, fontSize: 11, color: "#374151", textTransform: "uppercase", letterSpacing: "0.07em", borderBottom: "2px solid #E5E7EB", whiteSpace: "nowrap" };
const tdStyle = { padding: "10px 14px", fontSize: 13, borderRight: "1px solid #F3F4F6" };
